import { createContext, useContext, useMemo } from 'react';
import { ASSETS, getAsset, type AssetCode, type Fiat, type WalletDTO } from '@onyx/shared';

export const FiatContext = createContext<Fiat>('USD');
export const useFiat = () => useContext(FiatContext);

export interface Holding {
  asset: AssetCode;
  available: string;
  locked: string;
  amount: number;
  price: number;
  value: number;
}

export function holdings(wallet: WalletDTO | undefined, fiat: Fiat): Holding[] {
  if (!wallet) return [];
  return wallet.balances.map((b) => {
    const amount = Number(b.available) + Number(b.locked);
    const price = wallet.prices[b.asset]?.[fiat] ?? 0;
    return { asset: b.asset, available: b.available, locked: b.locked, amount, price, value: amount * price };
  });
}

export function usePortfolio(wallet: WalletDTO | undefined, fiat: Fiat) {
  return useMemo(() => {
    const list = holdings(wallet, fiat);
    const total = list.reduce((s, h) => s + h.value, 0);
    const held = list.filter((h) => h.amount > 0).sort((a, b) => b.value - a.value);
    return { list, total, held };
  }, [wallet, fiat]);
}

export function availableOf(wallet: WalletDTO | undefined, asset: string): string {
  return wallet?.balances.find((b) => b.asset === asset)?.available ?? '0';
}

export const assetDef = (code: string) => getAsset(code) ?? ASSETS[0]!;
